import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Send, Github, Linkedin, Twitter, Instagram } from 'lucide-react';
import { PortfolioConfig } from '../types/config';

interface ContactProps {
  config: PortfolioConfig;
}

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface FormSubmissionResponse {
  success: boolean;
  message?: string;
}

const Contact: React.FC<ContactProps> = ({ config }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
    setSubmitStatus('idle');
  }, []);

  const showNotification = useCallback((message: string, type: 'success' | 'error') => {
    // For now using alert, but you could implement a proper toast notification system
    alert(message);
  }, []);

  const validateForm = useCallback((data: FormData): boolean => {
    if (!data.name.trim()) {
      showNotification('Please enter your name', 'error');
      return false;
    }
    if (!data.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      showNotification('Please enter a valid email address', 'error');
      return false;
    }
    if (!data.subject.trim()) {
      showNotification('Please enter a subject', 'error');
      return false;
    }
    if (!data.message.trim()) {
      showNotification('Please enter a message', 'error');
      return false;
    }
    return true;
  }, [showNotification]);

  const submitFormData = useCallback(async (data: FormData): Promise<FormSubmissionResponse> => {
    if (!config.contact.formAccessKey) {
      throw new Error('Form access key not configured. Please set REACT_APP_FORM_ACCESS_KEY environment variable.');
    }

    const formData = new FormData();
    formData.append('access_key', config.contact.formAccessKey);
    formData.append('name', data.name.trim());
    formData.append('email', data.email.trim());
    formData.append('subject', data.subject.trim());
    formData.append('message', data.message.trim());

    const response = await fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  }, [config.contact.formAccessKey]);

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!validateForm(formData)) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await submitFormData(formData);
      
      if (response.success) {
        setSubmitStatus('success');
        showNotification('Thank you for your message! I\'ll get back to you soon.', 'success');
        resetForm();
      } else {
        setSubmitStatus('error');
        showNotification(response.message || 'Failed to send message. Please try again.', 'error');
      }
    } catch (error) {
      setSubmitStatus('error');
      console.error('Form submission error:', error);
      showNotification('An error occurred while sending your message. Please try again later.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateForm, submitFormData, showNotification, resetForm]);

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: config.contact.email,
      href: `mailto:${config.contact.email}`
    },
    {
      icon: MapPin,
      label: 'Location',
      value: config.personal.location,
      href: '#'
    }
  ];

  const socialLinks = [
    { icon: Github, href: config.contact.github, label: 'GitHub' },
    { icon: Linkedin, href: config.contact.linkedin, label: 'LinkedIn' },
    { icon: Twitter, href: config.contact.twitter, label: 'Twitter' },
    { icon: Instagram, href: config.contact.instagram, label: 'Instagram' }
  ];

  return (
    <section id="contact" className="section-padding bg-dark-50 dark:bg-dark-800">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Get In <span className="gradient-text">Touch</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-600 to-purple-600 mx-auto"></div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-semibold text-dark-800 dark:text-white mb-8">
              Let's work together
            </h3>
            <p className="text-dark-600 dark:text-dark-300 mb-8 leading-relaxed">
              I'm always interested in hearing about new opportunities, interesting projects, 
              or just want to say hello. Feel free to reach out!
            </p>

            {/* Contact Info */}
            <div className="space-y-6 mb-8">
              {contactInfo.map((info, index) => (
                <motion.a
                  key={info.label}
                  href={info.href}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center space-x-4 p-4 bg-white dark:bg-dark-900 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 flex-shrink-0 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
                    <info.icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-dark-800 dark:text-white">{info.label}</p>
                    <p className="text-dark-600 dark:text-dark-300 break-words">{info.value}</p>
                  </div>
                </motion.a>
              ))}
            </div>

            {/* Social Links */}
            <div>
              <h4 className="font-semibold text-dark-800 dark:text-white mb-4">Follow me</h4>
              <div className="flex space-x-4">
                {socialLinks.map((social, index) => (
                  social.href && (
                    <motion.a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                    viewport={{ once: true }}
                    className="w-12 h-12 bg-white dark:bg-dark-900 rounded-lg shadow-sm hover:shadow-md flex items-center justify-center text-dark-600 dark:text-dark-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-300"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <social.icon className="w-6 h-6" />
                  </motion.a>
                  )
                ))}
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-dark-200 dark:border-dark-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-dark-900 text-dark-900 dark:text-white placeholder-dark-500 dark:placeholder-dark-400"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-dark-200 dark:border-dark-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-dark-900 text-dark-900 dark:text-white placeholder-dark-500 dark:placeholder-dark-400"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-dark-200 dark:border-dark-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-dark-900 text-dark-900 dark:text-white placeholder-dark-500 dark:placeholder-dark-400"
                  placeholder={config.contact.subjectPlaceholder}
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-dark-200 dark:border-dark-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-dark-900 text-dark-900 dark:text-white placeholder-dark-500 dark:placeholder-dark-400 resize-none"
                  placeholder={config.contact.messagePlaceholder}
                />
              </div>
              
              <motion.button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Send Message</span>
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact; 